import type { PostOfficeContextValue } from '../post-office-context/types';
import type { MessageContextData } from './message-context';
import type {
	MessageInstanceDropDetails,
	MessageInstanceEventClient,
} from './message-instance-event-client';
import {
	getMissingMessageInstanceEventContext,
	hasCompleteMessageInstanceEventContext,
	recommendationFromContext,
	type ReportMissingContextArgs,
} from './message-instance-event-context';
import type { RecommendationContext } from './recommendation-context';

/** What a dropped event takes from the placement, shared by every message it offers. */
export type DroppedEventPlacement = {
	client: Pick<MessageInstanceEventClient, 'sendMessageInstanceEvent'>;
	browserContext: PostOfficeContextValue;
	placementId?: string;
	traceId?: string;
	reportMissingEventContext: (args: ReportMissingContextArgs) => void;
};

/**
 * The message a dropped event is about. `useMessageInstanceEvent` reads it from the message's
 * contexts; a placement choosing between several messages passes each message's own.
 */
export type DroppedMessageIdentity = Pick<
	MessageContextData,
	| 'messageInstanceId'
	| 'messageTemplateId'
	| 'triggerId'
	| 'messageCreationType'
	| 'journeyId'
	| 'journeyTriggerId'
	| 'configSetId'
> & {
	transactionAccountId?: string | null;
	analyticsDetails?: MessageContextData['analyticsDetails'] | null;
	recommendation: RecommendationContext;
};

/**
 * Sends a `dropped` message instance event, or reports the missing context when the message
 * can't be identified.
 */
export const sendMessageInstanceDropped = async (
	{
		client,
		browserContext,
		placementId,
		traceId,
		reportMissingEventContext,
	}: DroppedEventPlacement,
	message: DroppedMessageIdentity,
	{ dropReason, narrowExposureExperiment }: MessageInstanceDropDetails,
): Promise<void> => {
	const eventContext = {
		messageInstanceId: message.messageInstanceId,
		messageTemplateId: message.messageTemplateId,
		placementId,
		messageCreationType: message.messageCreationType,
		messageTriggerId: message.triggerId,
	};

	if (!hasCompleteMessageInstanceEventContext(eventContext)) {
		reportMissingEventContext({
			messageEventType: 'dropped',
			missingFields: getMissingMessageInstanceEventContext(eventContext),
			messageTemplateId: message.messageTemplateId,
			placementId,
		});
		return;
	}

	await client.sendMessageInstanceEvent({
		messageEventType: 'dropped',
		messageInstanceId: eventContext.messageInstanceId,
		messageTemplateId: eventContext.messageTemplateId,
		deliveryChannel: 'in-app',
		placementId: eventContext.placementId,
		channelType: 'pull',
		context: {
			...browserContext,
			source: eventContext.placementId,
			recommendation: recommendationFromContext(message.recommendation),
		},
		dropReason,
		narrowExposureExperiment,
		additionalMetadataFromFrontend: {
			transactionAccountId: message.transactionAccountId ?? null,
			...message.analyticsDetails,
		},
		...(traceId ? { placementTraceId: traceId } : {}),
		messageTriggerId: eventContext.messageTriggerId,
		journeyId: message.journeyId,
		journeyTriggerId: message.journeyTriggerId,
		messageCreationType: eventContext.messageCreationType,
		configSetId: message.configSetId,
		timestamp: Date.now(),
	});
};
